import { Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useActionsTresorerie, useCandidatsAffectation, useComptesTresorerie, type CompteSaisie } from '@/api/hooks';
import type { ResumeCompte } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader } from '@/components/common';
import { OngletsTresorerie, aujourdhui, estCaissier, estDirecteurFinancier, estResponsableTresorerie } from '@/components/tresorerie';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { cn, dateFr, montant } from '@/lib/utils';

const message = (e: unknown) => (e instanceof Error ? e.message : 'Action impossible.');

function DialogueCompte({ ouvert, onOpenChange, existant }: { ouvert: boolean; onOpenChange: (o: boolean) => void; existant?: ResumeCompte }) {
  const actions = useActionsTresorerie();
  const [v, setV] = useState<CompteSaisie>(existant ? { code: existant.code, type: existant.type, banque: existant.banque, intitule: existant.intitule, numero: existant.numero ?? '', devise: existant.devise, soldeMinimum: existant.soldeMinimum, plafondOperation: existant.plafondOperation ?? undefined, soldeMaximum: existant.soldeMaximum ?? undefined, actif: existant.actif } : { code: '', type: 'BANQUE', banque: '', intitule: '', numero: '', devise: 'USD', soldeMinimum: 0 });
  const maj = (patch: Partial<CompteSaisie>) => setV({ ...v, ...patch });
  const nombre = (s: string) => (s === '' ? undefined : Number(s));
  const enregistrer = async () => {
    try {
      if (existant) {
        const { code: _c, type: _t, ...reste } = v;
        void _c;
        void _t;
        await actions.modifierCompte.mutateAsync({ id: existant.id, corps: { ...reste, numero: reste.numero || undefined } });
        toast.success('Compte modifié.');
      } else {
        await actions.creerCompte.mutateAsync({ ...v, code: v.code.trim().toUpperCase(), numero: v.numero || undefined });
        toast.success('Compte créé.');
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{existant ? `Modifier ${existant.code}` : 'Nouveau compte du Fonds'}</DialogTitle>
          <DialogDescription>Huit comptes bancaires au plus et une caisse. Le numéro de compte n'est visible que du responsable et du Directeur financier.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {!existant && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="code">Code</Label>
                <Input id="code" value={v.code} onChange={(e) => maj({ code: e.target.value })} placeholder="RAW-USD" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="type">Type</Label>
                <SelectNatif id="type" value={v.type} onChange={(e) => maj({ type: e.target.value as 'BANQUE' | 'CAISSE' })}>
                  <option value="BANQUE">Compte bancaire</option>
                  <option value="CAISSE">Caisse</option>
                </SelectNatif>
              </div>
            </>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="banque">Banque (ou « Caisse »)</Label>
            <Input id="banque" value={v.banque} onChange={(e) => maj({ banque: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="intitule">Intitulé</Label>
            <Input id="intitule" value={v.intitule} onChange={(e) => maj({ intitule: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="numero">Numéro de compte</Label>
            <Input id="numero" value={v.numero ?? ''} onChange={(e) => maj({ numero: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="devise">Devise</Label>
            <SelectNatif id="devise" value={v.devise} onChange={(e) => maj({ devise: e.target.value })} disabled={Boolean(existant)}>
              <option value="USD">USD</option>
              <option value="CDF">CDF</option>
              <option value="EUR">EUR</option>
            </SelectNatif>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="minimum">Solde minimum (alerte)</Label>
            <Input id="minimum" type="number" min={0} step="0.01" value={v.soldeMinimum ?? ''} onChange={(e) => maj({ soldeMinimum: nombre(e.target.value) })} />
          </div>
          {v.type === 'CAISSE' && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="plafond">Plafond par paiement en espèces</Label>
                <Input id="plafond" type="number" min={0} step="0.01" value={v.plafondOperation ?? ''} onChange={(e) => maj({ plafondOperation: nombre(e.target.value) })} placeholder="paramètre général si vide" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="maximum">Solde maximum de la caisse</Label>
                <Input id="maximum" type="number" min={0} step="0.01" value={v.soldeMaximum ?? ''} onChange={(e) => maj({ soldeMaximum: nombre(e.target.value) })} placeholder="paramètre général si vide" />
              </div>
            </>
          )}
          {existant && (
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <Checkbox checked={v.actif ?? true} onCheckedChange={(c) => maj({ actif: c === true })} /> Compte actif
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button onClick={enregistrer} disabled={actions.creerCompte.isPending || actions.modifierCompte.isPending || !v.banque.trim() || !v.intitule.trim() || (!existant && v.code.trim().length < 2)}>
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogueSolde({ compte, onOpenChange }: { compte: ResumeCompte | null; onOpenChange: (o: boolean) => void }) {
  const actions = useActionsTresorerie();
  const [solde, setSolde] = useState('');
  const [date, setDate] = useState(aujourdhui());
  const enregistrer = async () => {
    if (!compte) return;
    try {
      await actions.saisirSolde.mutateAsync({ id: compte.id, soldeReleve: Number(solde), dateReleve: date });
      toast.success('Solde du relevé enregistré.');
      onOpenChange(false);
      setSolde('');
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <Dialog open={Boolean(compte)} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Solde du relevé · {compte?.code}</DialogTitle>
          <DialogDescription>Solde affiché par la plateforme bancaire (ou solde de l'arrêté de caisse). Les paiements confirmés après cette date s'en déduisent pour le disponible.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="solde">Solde ({compte?.devise})</Label>
            <Input id="solde" type="number" step="0.01" value={solde} onChange={(e) => setSolde(e.target.value)} autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dateReleve">Date du relevé</Label>
            <Input id="dateReleve" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button onClick={enregistrer} disabled={actions.saisirSolde.isPending || solde === '' || !date}>
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Affectations({ c, gestion }: { c: ResumeCompte; gestion: boolean }) {
  const actions = useActionsTresorerie();
  const agents = useCandidatsAffectation(c.type, gestion);
  const [agentId, setAgentId] = useState('');
  const [suppleant, setSuppleant] = useState(false);
  const candidats = (agents.data ?? []).filter((a) => !c.tresoriers.some((t) => t.agentId === a.id));
  const affecter = async () => {
    try {
      await actions.affecter.mutateAsync({ id: c.id, agentId, suppleant });
      toast.success('Affectation enregistrée.');
      setAgentId('');
      setSuppleant(false);
    } catch (e) {
      toast.error(message(e));
    }
  };
  const retirer = async (affectationId: string) => {
    try {
      await actions.retirerAffectation.mutateAsync({ id: c.id, affectationId });
      toast.success('Affectation retirée.');
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <div className="space-y-2 text-sm">
      <div className="font-medium">{c.type === 'CAISSE' ? 'Caissier(s)' : 'Trésorier(s) affecté(s)'}</div>
      {c.tresoriers.length === 0 ? (
        <div className="text-muted-foreground">Aucune affectation : les paiements de ce compte ne peuvent pas être transmis.</div>
      ) : (
        <ul className="space-y-1">
          {c.tresoriers.map((t) => (
            <li key={t.affectationId} className="flex items-center justify-between gap-2">
              <span>
                {t.nom} {t.suppleant && <Badge variant="outline">suppléant</Badge>}
              </span>
              {gestion && (
                <Button variant="ghost" size="sm" onClick={() => retirer(t.affectationId)}>
                  Retirer
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {gestion && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <SelectNatif value={agentId} onChange={(e) => setAgentId(e.target.value)} className="min-w-[16rem] flex-1" aria-label="Agent à affecter">
            <option value="">Affecter un {c.type === 'CAISSE' ? 'caissier' : 'trésorier'}</option>
            {candidats.map((a) => (
              <option key={a.id} value={a.id}>
                {a.nom}{a.structureCode ? ` · ${a.structureCode}` : ''}
              </option>
            ))}
          </SelectNatif>
          <label className="flex items-center gap-2 text-xs">
            <Checkbox checked={suppleant} onCheckedChange={(x) => setSuppleant(x === true)} /> suppléant
          </label>
          <Button size="sm" variant="outline" onClick={affecter} disabled={!agentId || actions.affecter.isPending}>
            Affecter
          </Button>
        </div>
      )}
    </div>
  );
}

/** Comptes du Fonds (RT-5) : soldes, relevés, affectations des trésoriers ; création et modification par le responsable ou le DF. */
export function ComptesTresorerie() {
  const session = useSession();
  const profils = session?.utilisateur.profils ?? [];
  const gestion = estResponsableTresorerie(profils) || estDirecteurFinancier(profils);
  const [inactifs, setInactifs] = useState(false);
  const { data, isLoading, error } = useComptesTresorerie(inactifs);
  const [creation, setCreation] = useState(false);
  const [modification, setModification] = useState<ResumeCompte | null>(null);
  const [solde, setSolde] = useState<ResumeCompte | null>(null);
  const totalDisponible = (data ?? []).reduce((s, c) => s + c.soldeDisponible, 0);
  const totalPrevisionnel = (data ?? []).reduce((s, c) => s + c.soldePrevisionnel, 0);

  return (
    <div>
      <PageHeader
        titre="Comptes du Fonds"
        sousTitre={data ? `Disponible total ${montant(totalDisponible, true)} · prévisionnel fin de semaine ${montant(totalPrevisionnel, true)}` : undefined}
        actions={
          <>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={inactifs} onCheckedChange={(c) => setInactifs(c === true)} /> comptes inactifs
            </label>
            {gestion && (
              <Button onClick={() => setCreation(true)}>
                <Plus className="size-4" /> Compte
              </Button>
            )}
          </>
        }
      />
      <OngletsTresorerie />
      {isLoading ? (
        <Chargement />
      ) : error || !data ? (
        <ErreurBloc erreur={error} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {data.map((c) => (
            <Card key={c.id} className={cn(!c.actif && 'opacity-60')}>
              <CardHeader>
                <CardTitle className="flex flex-wrap items-center gap-2">
                  {c.banque} · {c.code}
                  <Badge variant="secondary">{c.type === 'CAISSE' ? 'Caisse' : c.devise}</Badge>
                  {c.sousMinimum && <Badge variant="destructive">Sous le minimum</Badge>}
                  {!c.actif && <Badge variant="outline">inactif</Badge>}
                </CardTitle>
                <CardDescription>
                  {c.intitule}
                  {c.numero ? ` · n° ${c.numero}` : ''}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3">
                  <div>
                    <dt className="text-xs text-muted-foreground">Relevé {c.dateReleve ? `du ${dateFr(c.dateReleve)}` : ''}</dt>
                    <dd className="num font-medium">{montant(c.soldeReleve, true)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Payés non au relevé</dt>
                    <dd className="num">{montant(c.payesNonAuReleve, true)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Disponible</dt>
                    <dd className={cn('num font-semibold', c.soldeDisponible < 0 && 'text-red-700')}>{montant(c.soldeDisponible, true)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Postés en attente</dt>
                    <dd className="num">{montant(c.postesEnAttente, true)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Programmés cette semaine</dt>
                    <dd className="num">{montant(c.programmesSemaine, true)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Prévisionnel · minimum {montant(c.soldeMinimum)}</dt>
                    <dd className={cn('num font-semibold', c.sousMinimum && 'text-red-700')}>{montant(c.soldePrevisionnel, true)}</dd>
                  </div>
                  {c.type === 'CAISSE' && (
                    <div className="col-span-2 sm:col-span-3">
                      <dt className="text-xs text-muted-foreground">Plafond par paiement · solde maximum</dt>
                      <dd className="num">
                        {c.plafondOperation === null ? 'paramètre général' : montant(c.plafondOperation)} · {c.soldeMaximum === null ? 'paramètre général' : montant(c.soldeMaximum)}
                      </dd>
                    </div>
                  )}
                </dl>
                <Affectations c={c} gestion={gestion} />
                <div className="flex flex-wrap gap-2">
                  {(gestion || (c.type === 'CAISSE' && estCaissier(profils))) && (
                    <Button size="sm" variant="outline" onClick={() => setSolde(c)}>
                      Saisir le solde du relevé
                    </Button>
                  )}
                  {gestion && (
                    <Button size="sm" variant="ghost" onClick={() => setModification(c)}>
                      Modifier
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {creation && <DialogueCompte ouvert onOpenChange={(o) => !o && setCreation(false)} />}
      {modification && <DialogueCompte key={modification.id} ouvert onOpenChange={(o) => !o && setModification(null)} existant={modification} />}
      <DialogueSolde compte={solde} onOpenChange={(o) => !o && setSolde(null)} />
    </div>
  );
}
